import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { selectedCustomer } from '@app/ngrx-customer/store';
import { Message } from '@app/ngrx-message/models/client/message';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { ModalController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import { ToastParams } from '@usf/ngrx-inventory';
import {
  ACCESSIBLE_LIST_ACTIONS,
  DownloadListOptions,
  LIST_ACTIONS,
} from '@usf/ngrx-list';
import { ProductPricingService } from '@usf/ngrx-pricing';
import {
  BetterBuysActions,
  LoadingState,
  ProductStateService,
  selectProductDetails,
} from '@usf/ngrx-product';
import { combineLatest, iif, mergeMap, of } from 'rxjs';
import {
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { BetterBuysConstants } from '../../../better-buys/constants/better-buys-constants.enum';
import { MessageTypeEnum } from '../../../ngrx-message/constants/messageTypeEnum';
import { PATHS } from '../../../shared/constants/paths';
import { DownloadListService } from '../../../shared/services/document/download-list/download-list.service';
import { LoadingSpinnerService } from '../../../shared/services/loading-spinner/loading-spinner.service';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { ListsService } from '../../services/lists.service';
import { DeleteListSummaryModalComponent } from '../../shared/delete-list-summary-modal/delete-list-summary-modal.component';
import { createListKey } from '../../shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { ListDetailManagementService } from '../../shared/list-detail-management/services/list-detail-management.service';
import { selectGroupsByListKey } from '../../shared/list-detail-management/selectors/list-detail-management-view-model.selector';
import { productByIdSelector } from '@shared/selectors/product.selectors';

@Injectable({
  providedIn: 'root',
})
export class ClientListEffects {
  /* Create list synchrously was successful */
  createListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.createSuccess),
        switchMap(action => this.getDataForCreateListSuccess(action)),
        tap(({ action, listGroups, favoriteProduct }) => {
          if (action.favoriteProductNumber) {
            this.listDetailManagementService.addProductToFavoritesList(
              action.list,
              listGroups,
              favoriteProduct,
            );
          } else {
            this.loadingSpinnerService.dismissSpinnerModal();
            this.listsService.goToShoppingList([
              'lists',
              'management',
              action.list.listKey.listTypeId + '-' + action.list.listKey.listId,
            ]);
          }
        }),
      ),
    { dispatch: false },
  );

  /* Edit list type synchronously was successful */
  editListTypeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.editListTypeSuccess),
        tap(action => {
          const listType = action.newListType.toLowerCase();
          const listName = action.listName;
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant('i18n.lists.editListTypeSuccess', {
              listType,
              listName,
            }),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  /* Edit list name synchronously was successful */
  editListNameSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.editListNameSuccess),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
        }),
      ),
    { dispatch: false },
  );

  /* Delete group and move items to unassigned group was successful */
  deleteGroupAndMoveSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.deleteGroupAndMoveSuccess),
        tap(action => {
          this.loadingSpinnerService
            .dismissSpinnerModal()
            .then(() => this.scrollToUnassignedGroup());
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.lists.deleteGroupAndMoveItemsSuccess',
              {
                groupName: action.groupName,
              },
            ),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  /* Delete group and all the items in it was successful */
  deleteGroupAndDeleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.deleteGroupAndDeleteSuccess),
        tap(action => {
          this.loadingSpinnerService
            .dismissSpinnerModal()
            .then(() => this.scrollToUnassignedGroup());
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.lists.deleteGroupAndDeleteItemsSuccess',
              {
                groupName: action.groupName,
              },
            ),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  /* Edit group sequence was successful */
  editGroupSequenceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.editGroupSequenceSuccess),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant('i18n.lists.groupMovedSuccess'),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  /* Delete list was successful */
  deleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.deleteSuccess),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant('i18n.lists.deleteListSuccess', {
              listName: action.listName,
            }),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  /* Domain api call failed for deleteGroup, create list, editGroupSequence or edit list */
  domainCallFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LIST_ACTIONS.deleteGroupFail,
          LIST_ACTIONS.editListTypeFail,
          LIST_ACTIONS.editListNameFail,
          LIST_ACTIONS.createFail,
          LIST_ACTIONS.editGroupSequenceFail,
          LIST_ACTIONS.deleteFail,
        ),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant('i18n.lists.genericError'),
            'red-toast',
            MessageTypeEnum.error,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  /* Better Buy Accept was Successful */
  betterBuyAcceptSwapSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LIST_ACTIONS.betterBuyAcceptSuccess),
      map(action => {
        this.loadingSpinnerService.dismissSpinnerModal();
        this.toastService.presentToastMsg(
          this.translateService.instant('i18n.betterBuys.acceptSwapSuccess'),
          'green-toast',
          MessageTypeEnum.success,
          [],
        );
        const betterBuyItem = action.betterBuyItems[0];
        // Only remove better buy from state if the mule call succeeded
        if (
          action.muleErrorNote !==
            BetterBuysConstants.acceptSuccessWithMuleFail &&
          action.muleErrorNote !==
            BetterBuysConstants.acceptLMASuccessWithMuleFail
        ) {
          this.store.dispatch(
            BetterBuysActions.acceptSuggestionAndMulesoftSuccess({
              oldNumber: betterBuyItem.oldProdNbr,
              newNumber: betterBuyItem.newProdNbr,
            }),
          );
        }
        return BetterBuysActions.acceptSuggestion({
          annualSavings: betterBuyItem.anulSavings,
        });
      }),
    ),
  );

  /* Better Buy Accept Failed */
  betterBuyAcceptSwapFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.betterBuyAcceptFail),
        map(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.betterBuys.acceptDeclineFailureMessage',
            ),
            'red-toast',
            MessageTypeEnum.error,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  /* Better Buy Decline was Successful */
  betterBuyDeclineSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LIST_ACTIONS.betterBuyDeclineSuccess),
      map(action => {
        this.loadingSpinnerService.dismissSpinnerModal();
        this.toastService.presentToastMsg(
          this.translateService.instant(
            'i18n.lists.betterBuyDeclineSuccessMessage',
          ),
          'green-toast',
          MessageTypeEnum.success,
          [],
        );
        return action;
      }),
      switchMap(action => [
        BetterBuysActions.declineSuggestion({
          oldNumber: action.betterBuyItems[0].oldProdNbr,
          newNumber: action.betterBuyItems[0].newProdNbr,
          annualSavings: action.betterBuyItems[0].anulSavings,
        }),
      ]),
    ),
  );

  /* Better Buy Decline Failed */
  betterBuyDeclineFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.betterBuyDeclineFail),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.betterBuys.acceptDeclineFailureMessage',
            ),
            'red-toast',
            MessageTypeEnum.error,
            [],
          );
        }),
      ),
    {
      dispatch: false,
    },
  );

  downloadTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.requestDownloadTemplate),
        tap(action => {
          this.downloadListService.donwloadImportTemplate();
        }),
      ),
    {
      dispatch: false,
    },
  );

  importListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.importListSuccess),
        tap(action => {
          this.listsService.goToShoppingList([
            'lists',
            'detail',
            action.importedListContents.list.listKey.listTypeId +
              '-' +
              action.importedListContents.list.listKey.listId,
          ]);
          this.loadingSpinnerService.dismissSpinnerModal();
          if (action.errorDetails.length === 0) {
            this.messageStoreService.upsertMessage({
              type: MessageTypeEnum.success,
              display: this.translateService.instant(
                'i18n.lists.importListModal.message.success',
                {
                  ['listName']: action.importedListContents.list.listName,
                } as ToastParams,
              ),
              toast: true,
              sticky: false,
            } as Message);
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  copyToListDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ACCESSIBLE_LIST_ACTIONS.loadFailure),
        tap(action => {
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.error,
            display: this.translateService.instant(
              'i18n.lists.copyProductsModal.failureGetData',
            ),
            toast: true,
            sticky: true,
          } as Message);
          if (
            this.platformService.platformEnum.mobile ===
            this.platformService.platformType
          ) {
            this.modalController.dismiss();
          } else {
            this.listDetailManagementService.closeCopyModal();
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  copyToListDataFailure2$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ACCESSIBLE_LIST_ACTIONS.loadSuccess),
        withLatestFrom(this.store.select(selectedCustomer)),
        tap(([action, selectedCustomer]) => {
          let onlyCustomerLists = true;
          action.lists.forEach(list => {
            if (list.customerNumber !== selectedCustomer.customerNumber) {
              onlyCustomerLists = false;
            }
          });
          if (
            action.lists.length <= 0 ||
            (action.lists.length === 1 && onlyCustomerLists)
          ) {
            this.messageStoreService.upsertMessage({
              type: MessageTypeEnum.error,
              display: this.translateService.instant(
                'i18n.lists.copyProductsModal.failureGetData',
              ),
              toast: true,
              sticky: true,
            } as Message);
            if (
              this.platformService.platformEnum.mobile ===
              this.platformService.platformType
            ) {
              this.modalController.dismiss();
            } else {
              this.listDetailManagementService.closeCopyModal();
            }
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  importListFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.importListFail),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.error,
            display: this.translateService.instant(
              'i18n.lists.importListModal.message.errorApi',
              { ['listName']: action.listName } as ToastParams,
            ),
            toast: true,
            sticky: true,
          } as Message);
        }),
      ),
    {
      dispatch: false,
    },
  );

  downloadList$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.downloadList),
        // Load products and prices
        tap(action => {
          this.loadProductsAndPricingForList(action.downloadListOptions);
        }),
        // Make sure product/price data is loaded before attempting to download
        mergeMap(action => {
          const loadingSelector = action.downloadListOptions
            .includeProductPrices
            ? this.listDetailManagementService.isListAndProductAndPricingDataLoaded$(
                action.downloadListOptions.listTypeId,
                String(action.downloadListOptions.listId),
                action.downloadListOptions.includeNutritionals,
              )
            : this.listDetailManagementService.selectListProductDataLoadingState$(
                action.downloadListOptions.listTypeId,
                String(action.downloadListOptions.listId),
                action.downloadListOptions.includeNutritionals,
              );
          return combineLatest([of(action), loadingSelector]).pipe(
            filter(([action, loadingState]) => {
              return (
                loadingState === LoadingState.loaded || loadingState === true
              );
            }),
            take(1),
          );
        }),
        // Select view model so that we have the array of data to download
        mergeMap(([action, loadingState]) => {
          return combineLatest([
            of(action),
            this.listDetailManagementService.selectListDetailVm$(
              action.downloadListOptions.listTypeId,
              String(action.downloadListOptions.listId),
            ),
          ]).pipe(take(1));
        }),
        mergeMap(([action, viewmodel]) => {
          return combineLatest([
            of(action),
            of(viewmodel),
            iif(
              () => action.downloadListOptions.includeNutritionals,
              this.store.select(
                selectProductDetails(
                  viewmodel.listItems.map(item => item.productNumber),
                ),
              ),
              of([]),
            ),
          ]).pipe(take(1));
        }),
        tap(([action, viewModel, details]) => {
          this.downloadListService.downloadList(
            action.downloadListOptions,
            viewModel?.list?.listState,
            viewModel?.items,
            details,
          );
        }),
      ),
    {
      dispatch: false,
    },
  );

  copySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.copySuccess),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal().then(() => {
            if (!action.list) {
              this.toastService.presentToastMsg(
                this.translateService.instant('i18n.lists.listCreated'),
                'green-toast',
                MessageTypeEnum.success,
                [],
              );
            } else {
              this.toastService.presentToastMsg(
                this.translateService.instant('i18n.lists.copyListSuccess', {
                  listName: action.list?.listName,
                }),
                'green-toast',
                MessageTypeEnum.success,
                [
                  {
                    text: this.translateService.instant('i18n.lists.goToList'),
                    side: 'end',
                    handler: () => {
                      this.listsService.goToShoppingList([
                        'lists',
                        'detail',
                        createListKey(action.list.listKey),
                      ]);
                    },
                  },
                ],
              );
            }
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  copyFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.copyFail),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal().then(() => {
            this.toastService.presentToastMsg(
              this.translateService.instant('i18n.lists.copyListFail', {
                listName: action.listName,
              }),
              'red-toast',
              MessageTypeEnum.error,
              [
                {
                  icon: 'close-circle',
                  role: 'cancel',
                },
              ],
              null,
            );
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  massDeleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.massDeleteSuccess),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.presentMassDeleteSuccessToast(action);
          const currentUrl = this.router.url;
          if (
            currentUrl?.includes('management') ||
            currentUrl?.includes('detail')
          ) {
            this.router.navigate([PATHS.LISTS]);
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  massDeleteFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ACTIONS.massDeleteFail),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant('i18n.lists.genericError'),
            'red-toast',
            MessageTypeEnum.error,
            [],
          );
        }),
      ),
    {
      dispatch: false,
    },
  );

  copyToListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ACCESSIBLE_LIST_ACTIONS.copyToMultipleSuccess),
        tap(action => {
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.lists.verifyProductsModal.success',
              {
                products: action.products,
                lists: action.lists,
                productPlural: action.products > 1 ? 's' : '',
                listsPlural: action.lists > 1 ? 's' : '',
              },
            ),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  copyToListFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ACCESSIBLE_LIST_ACTIONS.copyToMultipleFailure),
        tap(action => {
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.lists.verifyProductsModal.failure',
            ),
            'red-toast',
            MessageTypeEnum.error,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  presentMassDeleteSuccessToast(action) {
    this.toastService.presentToastMsg(
      this.translateService.instant(
        'i18n.lists.massDeleteModal.successToastMessage',
        {
          listName: action.listName,
        },
      ),
      'green-toast',
      MessageTypeEnum.success,
      [
        {
          text: this.translateService.instant(
            'i18n.lists.massDeleteModal.successToastButton',
          ),
          side: 'end',
          handler: async () => {
            const summaryModal = await this.modalController.create({
              component: DeleteListSummaryModalComponent,
              componentProps: {
                customerList: action.customerList,
                title: this.translateService.instant(
                  'i18n.lists.massDeleteModal.summaryTitle',
                  { listName: action.listName },
                ),
                platform: this.platformService.platformType,
                isConfirmation: false,
              },
              cssClass:
                this.platformService.platformType ===
                this.platformService.platformEnum.desktop
                  ? 'delete-list-summary-desktop'
                  : 'ion-modal-touch',
              mode: 'ios',
              initialBreakpoint:
                this.platformService.platformType !==
                this.platformService.platformEnum.desktop
                  ? 1
                  : undefined,
              breakpoints: [0, 1],
            });
            summaryModal.present();
          },
        },
        {
          icon: 'close-circle',
          role: 'cancel',
        },
      ],
      null,
    );
  }

  scrollToUnassignedGroup() {
    document.getElementById('list-groups-unassigned-button').scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }

  loadProductsAndPricingForList(downloadListOptions: DownloadListOptions) {
    this.listDetailManagementService
      .productNumbers$(
        downloadListOptions.listTypeId,
        String(downloadListOptions.listId),
      )
      .pipe(take(1))
      .subscribe(productNumbers => {
        const uniqueProducts = productNumbers.filter(
          (val, i) => productNumbers.indexOf(val) === i && !!val,
        );
        if (downloadListOptions.includeProductPrices) {
          this.productPricingService.getPrices(uniqueProducts);
        }

        if (downloadListOptions.includeNutritionals) {
          this.productStateService.loadProductDetails(uniqueProducts);
        }
        this.productStateService.loadProducts(uniqueProducts);
      });
  }

  getDataForCreateListSuccess(action: any) {
    if (!action.favoriteProductNumber) {
      return of({ action, listGroups: [], favoriteProduct: undefined });
    }

    return this.store
      .select(
        selectGroupsByListKey(
          action.list?.listKey?.listTypeId,
          action.list?.listKey?.listId,
        ),
      )
      .pipe(
        switchMap(listGroups =>
          this.store
            .select(productByIdSelector(action.favoriteProductNumber))
            .pipe(
              map(favoriteProduct => ({ action, listGroups, favoriteProduct })),
            ),
        ),
      );
  }

  constructor(
    private actions$: Actions,
    private loadingSpinnerService: LoadingSpinnerService,
    private listsService: ListsService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private store: Store,
    private downloadListService: DownloadListService,
    private listDetailManagementService: ListDetailManagementService,
    private productPricingService: ProductPricingService,
    private productStateService: ProductStateService,
    private messageStoreService: MessageStoreService,
    private modalController: ModalController,
    private platformService: PlatformService,
    private router: Router,
  ) {}
}
